const translate = (t, key, fallback) => {
  if (typeof t === 'function') {
    return t(key, fallback);
  }

  return fallback;
};

const normalizeReason = (reason) => {
  if (reason === null || reason === undefined) {
    return '';
  }

  if (typeof reason === 'string') {
    return reason.trim();
  }

  if (typeof reason === 'object') {
    try {
      return JSON.stringify(reason);
    } catch (_error) {
      return String(reason).trim();
    }
  }

  return String(reason).trim();
};

const AUDIO_VIDEO_EXTRACTION_PATTERNS = [
  /video to audio conversion failed/i,
  /failed to extract audio from video/i,
  /error extracting audio from video/i,
  /could not extract audio from video/i,
];

const AUDIO_TIMEOUT_PATTERNS = [
  /audio processing timed out/i,
  /audio processing timeout/i,
  /timed out after .*attempt/i,
];

const AUDIO_UNSUPPORTED_PATTERNS = [
  /unsupported audio format/i,
  /audio format.*not supported/i,
  /audio not supported/i,
  /not a supported audio format/i,
];

const AUDIO_SERVICE_PATTERNS = [
  /unable to reach .*audio/i,
  /failed to reach .*audio/i,
  /audio service.*unavailable/i,
  /audio api.*error/i,
  /connection.*audio/i,
  /network.*audio/i,
];

const AUDIO_UNEXPECTED_RESPONSE_PATTERNS = [
  /invalid json response from audio model/i,
  /invalid response from audio model/i,
  /unable to parse audio response/i,
  /audio model returned invalid json/i,
  /audio model returned invalid response/i,
];

const AUDIO_GENERIC_PATTERNS = [
  /audio processing error/i,
  /audio processing failed/i,
  /error extracting text from audio/i,
  /failed to process audio chunk/i,
  /failed to assemble audio file/i,
  /audio transcription failed/i,
  /failed to transcribe audio/i,
  /audio ai model is disabled/i,
  /audio model is disabled/i,
  /audio file not found/i,
];

const matchesAny = (value, patterns) => patterns.some((pattern) => pattern.test(value));

export const formatFailureReason = (reason, t) => {
  const rawReason = normalizeReason(reason);
  if (!rawReason) {
    return '';
  }

  const normalizedReason = rawReason.toLowerCase();
  const looksLikeAudioReason =
    normalizedReason.includes('audio') ||
    normalizedReason.includes('voice') ||
    normalizedReason.includes('transcription') ||
    normalizedReason.includes('video to audio');

  if (matchesAny(normalizedReason, AUDIO_VIDEO_EXTRACTION_PATTERNS)) {
    return translate(
      t,
      'audio_failure_video',
      "We couldn't extract audio from this video. Please make sure the file contains an audio track and try again."
    );
  }

  if (looksLikeAudioReason && matchesAny(normalizedReason, AUDIO_TIMEOUT_PATTERNS)) {
    return translate(
      t,
      'audio_failure_timeout',
      "Audio processing took too long. Please try again with a shorter file or later."
    );
  }

  if (looksLikeAudioReason && matchesAny(normalizedReason, AUDIO_UNSUPPORTED_PATTERNS)) {
    return translate(
      t,
      'audio_failure_unsupported',
      "This audio format is not supported. Please try MP3, WAV, or M4A."
    );
  }

  if (looksLikeAudioReason && matchesAny(normalizedReason, AUDIO_SERVICE_PATTERNS)) {
    return translate(
      t,
      'audio_failure_service_unavailable',
      "The audio service is temporarily unavailable. Please try again later."
    );
  }

  if (looksLikeAudioReason && matchesAny(normalizedReason, AUDIO_UNEXPECTED_RESPONSE_PATTERNS)) {
    return translate(
      t,
      'audio_failure_unexpected_response',
      "The audio service returned an unexpected response. Please try again."
    );
  }

  if (looksLikeAudioReason && matchesAny(normalizedReason, AUDIO_GENERIC_PATTERNS)) {
    return translate(
      t,
      'audio_failure_generic',
      "We couldn't process this audio file. Please check the format and try again."
    );
  }

  return rawReason;
};
